import {Component, DestroyRef, inject, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {finalize, Observable, of} from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {itemTypesFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/item-types-filter.config';
import {ItemTypesFormComponent} from '../item-types-form/item-types-form.component';
import {MatDialog} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemType} from '@emm/core/models/item-type.model';
import {ItemTypeClass} from '@emm/core/models/item-type-class.model';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {ItemTypesFacadeService} from '@emm/management/services/item-types-facade.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {AddItemTypeDialogComponent} from '@emm/shared/dialogs/add-item-type-dialog/add-item-type-dialog.component';
import {SearchFilter} from '@emm/core/models/search-filter';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-item-types',
    styleUrls: ['./item-types.component.scss'],
    templateUrl: './item-types.component.html'
})
export class ItemTypesComponent implements OnInit {

    @ViewChild(ItemTypesFormComponent) itemTypesForm: ItemTypesFormComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    anyLoading$ = this.itemTypesFacade.anyLoading$;
    centerLoading$ = this.itemTypesFacade.centerLoading$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    itemDetail$ = this.itemTypesFacade.itemTypeDetail$;
    itemTypeClasses$ = this.itemTypesFacade.itemTypeClasses$;
    itemTypes$ = this.itemTypesFacade.itemTypes$;
    prevItemType: ItemType;
    rightLoading$ = this.itemTypesFacade.rightLoading$;
    itemTypesCache = [];
    itemTypesAsGenericItems$ = this.itemTypesFacade.itemTypesAsGenericItems$;

    fields$ = this.itemTypeClasses$.pipe(
        filter(RA.isNonEmptyArray),
        map((itemTypeClasses: ItemTypeClass[]) => [
            fieldMap['name'](),
            {
                ...fieldMap['classTag'](),
                templateOptions: {
                    ...fieldMap['classTag']().templateOptions,
                    options: [
                        ...itemTypeClasses.map((itemTypeClass) => ({
                            label: itemTypeClass.className,
                            value: itemTypeClass.classTag,
                        })),
                    ],
                },
            },
        ])
    );

    itemTypesContextMenuActions = [
        {
            // Delete
            enabled: R.T,
            click: (itemType) => this.onDeleteItemType(itemType),
            visible: this.isSuperAdmin ? R.T : R.F,
            icon: R.always('delete'),
            text: R.always('Delete'),
        },
    ] as ContextMenuAction<ItemType>[];

    constructor(
        private itemTypesFacade: ItemTypesFacadeService,
        private authFacade: AuthFacadeService,
        private dialogService: DialogService,
        private dialog: MatDialog,
        private notificationService: NotificationService,
        private userService: UserService,
    ) {
    }


    ngOnInit(): void {
        this.itemTypesFacade.init();
        this.itemTypes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.itemTypesAsGenericItems$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.anyLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getItemTypes();
    }


    getItemTypes(): void {
        this.itemTypesFacade.startProgressBar()
        this.itemTypesFacade.api.getAllItemTypes()
            .pipe(
                finalize(() => this.itemTypesFacade.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                this.itemTypesCache = result;
                this.itemTypes$.next(this.itemTypesCache);
            }
        );
    }


    openCreateItemTypeDialog(): void {
        const dialogRef = this.dialog.open(AddItemTypeDialogComponent, {
            width: '600px',
            data: {showDelete: false, itemTypeClasses: this.itemTypeClasses$},
        });

        dialogRef.afterClosed()
            .pipe(
                filter((result) => result && result === 'submit'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificationService.success('Added Successfully');
                    this.getItemTypes();
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }


    filterNodes(value: string, selected: string): void {
        this.itemTypes$.next(this.itemTypesCache);
        this.itemTypes$.pipe(
            take(1),
            map(list => {
                let filteredNodes;
                try {
                    switch (selected) {
                        case 'classTag':
                            filteredNodes = list.filter(nd =>
                                nd['source'][selected].toLowerCase() === value.toLowerCase());
                            break;
                        default:
                            filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                            break;
                    }
                    this.itemTypes$.next(filteredNodes);
                } catch (e) {}
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }


    onSelectItemType(selected: ItemType): void {
        if (RA.isNotNil(selected)) {
            this.checkIfItemHasChanges(this.prevItemType)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                    this.prevItemType = selected;
                    this.itemTypesFacade.selectItemType(selected);
                });
        } else {
            this.prevItemType = null;
        }
    }


    onDeleteItemType(itemType: ItemType): void {
        const dialogRef = this.dialogService.openConfirmDialog('Are you sure you want to delete',
            itemType.name, null);
        try {
            dialogRef.afterClosed()
                .pipe(
                    switchMap(result => result ? this.itemTypesFacade.api.deleteItemType(itemType.tag) : of(false)),
                    takeUntilDestroyed(this.destroyRef)
                ).subscribe(response => {
                if (response === false) {
                    this.notificationService.error(`Delete was cancelled`);
                } else {
                    this.notificationService.success(`Successfully deleted ${itemType.name}`);
                    this.getItemTypes();
                }
            });
        } catch (e) {
            this.notificationService.error(`Failed to delete ${itemType.name}`);
        }
    }


    checkIfItemHasChanges(prevItemType: ItemType): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevItemType)) {
                    return this.dialogService
                        .openSaveChangesDialog(prevItemType.name)
                        .afterClosed()
                        .pipe(
                            switchMap((res) => {
                                if (res) {
                                    this.itemTypesForm.submit(prevItemType.tag, false);
                                    return this.itemTypesFacade.updateItemType$ as Observable<boolean>;
                                }
                                this.itemTypesForm.cancel();
                                return of(true);
                            })
                        );
                }
                return of(true);
            })
        );
    }


    hasChanges(): boolean {
        return this.itemTypesForm && this.itemTypesForm.hasChanges();
    }


}
